'use client';

import { useEffect, useRef } from 'react';

import { toVideoSources } from 'helpers/video';

type TProps = {
  src: string;
  className?: string;
  // Only where no image sits behind the video; elsewhere the image is the poster.
  showPoster?: boolean;
};

// A muted background video that downloads nothing until it is on screen,
// plays only while it is visible, and stays still for visitors who ask for
// reduced motion. Phones and tablets get the 720p file.
const LazyVideo = (props: TProps) => {
  const { src, className, showPoster } = props;

  const ref = useRef<HTMLVideoElement>(null);

  const sources = toVideoSources(src);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    observer.observe(video);

    return () => observer.disconnect();
  }, [src]);

  return (
    <video
      key={src}
      ref={ref}
      className={className}
      poster={showPoster ? sources.poster : undefined}
      preload="none"
      loop
      muted
      playsInline
    >
      {sources.mobile && (
        <source
          src={sources.mobile}
          media="(max-width: 1023px)"
          type="video/mp4"
        />
      )}
      <source src={sources.desktop} type="video/mp4" />
    </video>
  );
};

export default LazyVideo;
